/**
 * Shared types and helpers for the billing screens.
 *
 * Every money field is a Decimal *string* from the API. Nothing here parses one
 * to a float for display: `formatCurrency` takes the string, and the balance is
 * computed with integer minor units so 0.1 + 0.2 stays 0.3.
 *
 * "Minor units" is not a synonym for "cents". KWD, BHD and OMR have three of
 * them and JPY has none, so every conversion below takes the currency code the
 * amount is denominated in. There is deliberately no default: a check, a
 * payment and a shift all carry their own `currency`, and an amount whose
 * currency is unknown is a bug to surface, not a 2 to assume.
 */
import { ChargeType, CheckStatus, PaymentMethod, decimalsFor } from '@/lib/shared-types';

/**
 * X-12 — all three are Prisma enums carried by `@/lib/shared-types`. Re-exported
 * so the billing screens keep one import, never redeclared.
 */
export { ChargeType, CheckStatus, PaymentMethod };

export const PAYMENT_METHODS: PaymentMethod[] = Object.values(PaymentMethod);

export const CHECK_STATUSES: CheckStatus[] = Object.values(CheckStatus);

export interface Charge {
  id: string;
  checkId: string;
  type: ChargeType;
  description: string;
  quantity: string;
  unitPrice: string;
  amount: string;
  taxAmount: string;
  voidedAt: string | null;
  voidReason: string | null;
  createdAt: string;
}

export interface Payment {
  id: string;
  checkId: string;
  shiftId: string | null;
  method: PaymentMethod;
  status: string;
  amount: string;
  tipAmount: string;
  refundedAmount: string;
  currency: string;
  reference: string | null;
  paidAt: string | null;
  createdAt: string;
}

export interface Check {
  id: string;
  branchId: string;
  /**
   * The dine-in visit this bill belongs to, which is how it reaches a table.
   * Null for a walk-up, a takeaway, or a resource session billed on its own.
   * It replaced `partyId`, and the difference is not only the name: a check
   * reached through a session cannot outlive that session, so the group seated
   * next at the same table opens their own bill and inherits nothing.
   */
  sessionId: string | null;
  number: string;
  isMaster: boolean;
  status: CheckStatus;
  subtotal: string;
  discountTotal: string;
  serviceTotal: string;
  taxTotal: string;
  tipTotal: string;
  grandTotal: string;
  paidTotal: string;
  currency: string;
  openedAt: string;
  closedAt: string | null;
  session?: {
    id: string;
    status: string;
    guestCount?: number;
    openedAt?: string;
    tableId?: string | null;
    table?: { id: string; number: string } | null;
  } | null;
  charges?: Charge[];
  payments?: Payment[];
  _count?: { charges: number; payments: number };
}

export interface CashierShift {
  id: string;
  branchId: string;
  userId: string;
  status: 'OPEN' | 'CLOSED';
  openingFloat: string;
  expectedCash: string;
  countedCash: string | null;
  cashIn: string;
  cashOut: string;
  openedAt: string;
  closedAt: string | null;
}

/**
 * A drawer movement's direction. `CashMovementType` is a Prisma enum, but unlike
 * `PaymentMethod` and `ChargeType` it is not carried by `@/lib/shared-types`
 * yet, so it is spelled out here rather than imported from a package that does
 * not export it.
 */
export type CashMovementType = 'IN' | 'OUT';

/** One line of `drawer.movements` — a single drop or pickup, with its narrative. */
export interface CashMovement {
  id: string;
  type: CashMovementType;
  /**
   * A raw Decimal string, NOT fixed-scale: the server sends the column as it is
   * stored, so a 200.00 movement arrives as `"200"` and 75.50 as `"75.5"`. Every
   * reader here goes through `toMinor`/`formatCurrency`, both of which take a
   * string, so the scale never has to be guessed.
   */
  amount: string;
  reason: string;
  /** Null when a device or an automation moved the cash, or the staff member has since been deleted. */
  actor: { id: string; name: string } | null;
  createdAt: string;
}

export interface ShiftSummary {
  shiftId: string;
  status: 'OPEN' | 'CLOSED';
  /**
   * S-26 — the currency the figures below are denominated in and the number of
   * minor units they are scaled to, as the SERVER resolved them from the
   * branch. The strip renders in the currency `useCurrency(branchId)` resolved
   * from the same endpoint, so these are a statement of what arrived rather
   * than something to re-derive from. Optional: an older API build omits them.
   */
  currency?: string;
  decimals?: number;
  openingFloat: string;
  cashIn: string;
  cashOut: string;
  expectedCash: string;
  countedCash: string | null;
  variance: string | null;
  byMethod: Array<{
    method: PaymentMethod;
    payments: number;
    amount: string;
    tips: string;
    refunded: string;
  }>;
  /**
   * S-01 — the drawer itemised from `CashMovement` rows.
   *
   * `cashIn`/`cashOut` here are the sum of the rows below; the shift's own
   * columns are still the authoritative totals. They differ for a drawer that
   * traded before movements were recorded, and the difference is reported as
   * `unitemisedCashIn`/`unitemisedCashOut` rather than quietly dropped — an old
   * shift with a real total and no rows behind it must not read as untouched.
   *
   * Optional because an older API build answers `/summary` without it.
   */
  drawer?: {
    cashIn: string;
    cashOut: string;
    unitemisedCashIn: string;
    unitemisedCashOut: string;
    movements: CashMovement[];
  };
}

/** Surface the API's own message rather than a generic failure line. */
export function apiError(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(message)) return message.join(' · ');
  return message ?? fallback;
}

/**
 * Decimal string → integer minor units, so subtraction never goes through a
 * float. `currency` decides how many digits a minor unit has — 1.234 is 1234
 * fils in KWD but 123 halalas in SAR.
 */
export function toMinor(value: string | null | undefined, currency: string): number {
  if (!value) return 0;
  const digitsCount = decimalsFor(currency);
  const [whole, fraction = ''] = String(value).split('.');
  const sign = whole.trim().startsWith('-') ? -1 : 1;
  const digits = `${whole.replace('-', '')}${(fraction + '0'.repeat(digitsCount)).slice(
    0,
    digitsCount,
  )}`;
  return sign * (Number(digits) || 0);
}

/** Integer minor units → the decimal string the API and `formatCurrency` speak. */
export function fromMinor(minor: number, currency: string): string {
  const digitsCount = decimalsFor(currency);
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(minor);
  if (digitsCount === 0) return `${sign}${abs}`;
  const scale = 10 ** digitsCount;
  return `${sign}${Math.floor(abs / scale)}.${String(abs % scale).padStart(digitsCount, '0')}`;
}

/** What is still owed on a check, in the same decimal-string form. */
export function balanceOf(check: Pick<Check, 'grandTotal' | 'paidTotal' | 'currency'>): string {
  return (
    fromMinor(
      toMinor(check.grandTotal, check.currency) - toMinor(check.paidTotal, check.currency),
      check.currency,
    )
  );
}

/**
 * The `step` an `<input type="number">` needs to express exactly one minor unit.
 * `0.01` is wrong in Kuwait — it makes a fil unreachable — and wrong in Japan,
 * where there is no sub-yen amount to type at all.
 */
export function moneyStep(currency: string): string {
  const digits = decimalsFor(currency);
  return digits === 0 ? '1' : `0.${'0'.repeat(digits - 1)}1`;
}

/** §34 — every financial mutation carries a key so a replay is a no-op. */
export function idempotencyKey(prefix: string): string {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `dashboard:${prefix}:${random}`;
}
